export interface MapTapControllerOptions {
    maxMovementPx?: number;
    maxDurationMs?: number;
}

/** Shared touch/mouse tap recognizer; orbit drags and long presses are ignored. */
export class MapTapController {
    private readonly maxMovementPx: number;
    private readonly maxDurationMs: number;
    private activePointerId: number | null = null;
    private downX = 0;
    private downY = 0;
    private downAt = 0;
    private started = false;

    constructor(
        private readonly canvas: HTMLCanvasElement,
        private readonly pick: (clientX: number, clientY: number) => number | null,
        private readonly onTap: (id: number) => void,
        options: MapTapControllerOptions = {},
    ) {
        this.maxMovementPx = options.maxMovementPx ?? 12;
        this.maxDurationMs = options.maxDurationMs ?? 400;
    }

    start(): void {
        if (this.started) return;
        this.started = true;
        this.canvas.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
        this.canvas.addEventListener('pointerup', this.handlePointerUp, { passive: true });
        this.canvas.addEventListener('pointercancel', this.handlePointerCancel, { passive: true });
    }

    stop(): void {
        if (!this.started) return;
        this.started = false;
        this.activePointerId = null;
        this.canvas.removeEventListener('pointerdown', this.handlePointerDown);
        this.canvas.removeEventListener('pointerup', this.handlePointerUp);
        this.canvas.removeEventListener('pointercancel', this.handlePointerCancel);
    }

    private handlePointerDown = (event: PointerEvent): void => {
        if (this.activePointerId !== null) return;
        this.activePointerId = event.pointerId;
        this.downX = event.clientX;
        this.downY = event.clientY;
        this.downAt = performance.now();
    };

    private handlePointerUp = (event: PointerEvent): void => {
        if (event.pointerId !== this.activePointerId) return;
        this.activePointerId = null;
        const moved = Math.hypot(event.clientX - this.downX, event.clientY - this.downY);
        const duration = performance.now() - this.downAt;
        if (moved > this.maxMovementPx || duration > this.maxDurationMs) return;
        const id = this.pick(event.clientX, event.clientY);
        if (id !== null) this.onTap(id);
    };

    private handlePointerCancel = (event: PointerEvent): void => {
        if (event.pointerId === this.activePointerId) this.activePointerId = null;
    };
}
